import { useEffect, useState, useRef } from "preact/hooks";
import { Fragment, h, render } from "preact";
import { FreeMode, Mousewheel } from "swiper/modules";
import { Plus, Minus } from "../icons.jsx";
import storefront from "../storefront";
import * as queries from "../storefront/queries";
import stockMessage from "../stock-message";
import getSellingPlanId from "../getSellingPlanId.js";
import findCompatibleVariants from "../findCompatibleVariants.js";
import unwrapNodes from "../unwrapNodes.js";

const RelatedProductsContent = ({ data, collection }) => {
  const [cartItems, setCartItems] = useState(data?.items.reverse());
  const [relatedItems, setRelatedItems] = useState(null);
  const [productId, setProductId] = useState(cartItems[0]?.product_id);
  const [fullCartProducts, setFullCartProducts] = useState([]);
  const [open, setOpen] = useState(true);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const newCartItems = data?.items.reverse();
    setCartItems(newCartItems);
    setProductId(newCartItems[0]?.product_id);
  }, [data.items]);

  useEffect(() => {
    if (!relatedItems?.length) {
      setOpen(false);
    }
  }, [relatedItems]);

  const swiperParams = {
    slidesPerView: "auto",
    spaceBetween: 16,
    rebuildOnUpdate: true,
    mousewheel: {
      forceToAxis: true,
    },
  };

  const getProductsByCollection = async () => {
    if (!collection) return;
    let items = collection.split(",");
    getFullProducts(items);
  };

  const getRelatedImage = (item) => {
    let image_src = item.featured_image
      ? item.featured_image
      : item?.images
        ? item?.images[0].src
        : null;

    return (
      image_src && (
        <img
          src={getSizedImageUrl(image_src, "small")}
          alt={item.title}
          width="80"
          height="80"
        />
      )
    );
  };

  const getFullProducts = async (items) => {
    const [fullCartItems, fullItems] = await Promise.all([
      storefront
        .request({
          query: queries.products,
          variables: {
            ids: [
              ...cartItems.map((cartItem) =>
                encodeId(cartItem.product_id, "Product"),
              ),
            ],
          },
        })
        .then((response) => {
          return unwrapNodes(
            response.data.nodes.filter(
              (p) => p && !p.tags.includes("hide-from-search"),
            ),
          );
        }),
      storefront
        .request({
          query: queries.products,
          variables: {
            ids: [...items.map((id) => encodeId(id, "Product"))],
          },
        })
        .then((response) => {
          return unwrapNodes(
            response.data.nodes.filter(
              (p) => p && !p.tags.includes("hide-from-search"),
            ),
          );
        }),
    ]);

    const sortedFullCartItems = fullCartItems.sort((a, b) => {
      const indexA = cartItems.findIndex(
        (cartItem) => `gid://shopify/Product/${cartItem.product_id}` === a.id,
      );
      const indexB = cartItems.findIndex(
        (cartItem) => `gid://shopify/Product/${cartItem.product_id}` === b.id,
      );

      return indexB - indexA;
    });
    setFullCartProducts(sortedFullCartItems);

    const compatibleSizeProduct = sortedFullCartItems.find(
      (product) => product.compatibleSizes,
    );

    setRelatedItems(
      fullItems.map((item) => {
        let variant = item.variants[0];

        if (compatibleSizeProduct) {
          const { compatibleVariants } = findCompatibleVariants({
            compatibleModels: JSON.parse(
              compatibleSizeProduct.compatibleSizes.models,
            ),
            compatibleSizes: JSON.parse(
              compatibleSizeProduct.compatibleSizes.sizes,
            ),
            variants: item.variants,
          });

          variant = compatibleVariants[0];
        }

        item.compatibleVariant = variant;

        return item;
      }),
    );
  };

  const getProducts = () => {
    if (!productId) return;
    fetch(
      `/recommendations/products.json?product_id=${productId}&limit=5&intent=related`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    )
      .then((data) => data?.json())
      .then((response) => {
        if (!response) return;
        let items = response.products.filter(
          ({ id, handle }) =>
            !cartItems.some(
              (e) => e.product_id === id || e.properties?._bundle === handle,
            ),
        );
        items = items.map((item) => item.id);
        getFullProducts(items);
      });
  };

  const handleOpen = () => {
    setOpen(!open);
  };

  const handleResize = () => {
    setOpen(window.innerHeight > 800);
    setShow(window.innerHeight > 640);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(async () => {
    if (productId) {
      getProducts();
    } else {
      getProductsByCollection();
    }
  }, [cartItems]);

  const addRelatedItem = (variant) => {
    if (!variant) return;

    const inventoryReno = variant.inventoryReno
      ? parseInt(variant.inventoryReno)
      : null;
    const inventoryAtlanta = variant.inventoryAtlanta
      ? parseInt(variant.inventoryAtlanta)
      : null;

    const properties = {
      _inventoryReno: inventoryReno,
      _inventoryAtlanta: inventoryAtlanta,
      _selling_plan: getSellingPlanId(variant),
      _added_by: "RelatedProductsContent addRelatedItem",
    };

    window.addToCart(
      {
        id: variant.id.replace("gid://shopify/ProductVariant/", ""),
        quantity: 1,
        properties,
        selling_plan: getSellingPlanId(variant),
      },
      {
        onError: (response) => console.error(response),
      },
    );
  };

  console.log(`relatedItems`, relatedItems);

  return (
    show &&
    relatedItems &&
    relatedItems.length > 0 && (
      <Fragment>
        <button
          className={`main-cart__ymal__heading`}
          onClick={() => {
            handleOpen();
          }}
        >
          <span className="h5">You May Also Like</span>
          {open ? <Minus /> : <Plus />}
        </button>
        <div
          className={`main-cart__ymal__container ${open ? " is-open" : ""}`}
          onFocus={() => {
            setOpen(true);
          }}
        >
          <swiper-container
            {...swiperParams}
            modules={[FreeMode, Mousewheel]}
            className="related-products__container"
          >
            {relatedItems.map((item, index) => (
              <swiper-slide className="swiper-slide">
                <div className="product-form__option">
                  <label>
                    <a
                      href={item.url ? item.url : `/products/${item.handle}`}
                      className="product-form__option-image"
                    >
                      {getRelatedImage(item)}
                    </a>
                    <div className="product-form__option-text">
                      <div className="product-form__option-text-inner">
                        <span>{item.title}</span>
                        <span className="product-form__option-price">
                          {window.formatMoney(
                            item.price
                              ? item.price / 100
                              : item.compatibleVariant.priceV2.amount,
                          )}
                        </span>
                        <span
                          className="product-form__option-availability caption-small"
                          data-stock
                        >
                          {stockMessage(item.compatibleVariant)}
                        </span>
                        <span></span>
                      </div>
                      <button
                        className="product-related__add"
                        add-to-cart
                        name="form"
                        onClick={() => addRelatedItem(item.compatibleVariant)}
                        aria-label="Add product to cart"
                      >
                        <span>Add to Cart</span>
                        <span className="product-related__icon">
                          <Plus />
                        </span>
                      </button>
                    </div>
                  </label>
                </div>
              </swiper-slide>
            ))}
          </swiper-container>
        </div>
      </Fragment>
    )
  );
};

export default RelatedProductsContent;
